import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { DOC_KEYS } from '../utils/docVocab';
import { cleanBillLines, mergeRateTable, dropEmptyLines } from '../utils/billLines';
import { resolveRateUnit, splitJurisdictionLines } from '../reports/taxRate';
import { moneyNum } from '../utils/money';
import JurisdictionBreakdown from './JurisdictionBreakdown';

// ═══ SCAN A FILED ORIGINAL BILL AS THE PRE-APPEAL REFERENCE — AND NOTHING ELSE ═══
// (Philip, 2026-10-09.) Original (pre-appeal) bills are filed as documents without scanning; only the revised bill
// is scanned and live. Savings read off the bills (utils/billSavings, method B: original − revised) need the
// original's lines. This reads an ALREADY-FILED document and stores its lines in tax_bill_references:
//   • no tax_bills row — so it never becomes the live bill, never supersedes, never reaches Approvals or a TBT;
//   • no file_exchange row — the filed document is read where it is (its storage_path) and referenced by id;
//   • the same two extraction questions and line clean-up as the scanner (taxbill + rate_table → mergeRateTable →
//     cleanBillLines), so the lines are read the same way the live bill's were.
// OR, quicker: TYPE the original's printed total ad valorem, with an optional note (source 'entered'). Savings then
// = entered original − the revised bill's ad valorem. Same table, same guarantees.
const num = (v) => moneyNum(v);
const fmtc = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }));

async function extract(storagePath, docType, token) {
  const res = await fetch('/api/extract', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ storage_path: storagePath, docType }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.error) throw new Error((body && body.error) || `extraction failed (${res.status})`);
  return body.result || {};
}

export default function ReferenceBillScan({ parcel, year, staffUser, isDark, onClose, onSaved }) {
  const c = isDark
    ? { bg: '#151c2e', border: '#374060', text: '#e8eaf2', muted: '#a8b0cc' }
    : { bg: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)' };
  const [docs, setDocs] = useState(null);
  const [liveIds, setLiveIds] = useState(new Set());
  const [picked, setPicked] = useState(null);
  const [mode, setMode] = useState('scan');             // scan | enter
  const [typed, setTyped] = useState({ total: '', note: '' });
  const [phase, setPhase] = useState('pick');          // pick | scanning | review | saving
  const [result, setResult] = useState(null);          // { lines, removed, paired, taxYear, taxable, extracted }
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      // The parcel's filed tax-bill documents: parcel-level, plus property-level ones (which show on every parcel).
      const vis = parcel.property_id
        ? `parcel_id.eq.${parcel.id},and(property_id.eq.${parcel.property_id},parcel_id.is.null)` : `parcel_id.eq.${parcel.id}`;
      const [{ data: fx, error: fe }, { data: live, error: le }] = await Promise.all([
        supabase.from('file_exchange').select('id,file_name,storage_path,year_subfolder,filed_at')
          .or(vis).eq('folder_key', DOC_KEYS.taxBills).order('filed_at', { ascending: false }),
        supabase.from('tax_bills').select('file_exchange_id').eq('parcel_id', parcel.id).eq('tax_year', String(year)).is('superseded_at', null),
      ]);
      if (fe || le) { setError(`Could not load the filed documents: ${(fe || le).message}`); setDocs([]); return; }
      setLiveIds(new Set((live || []).map((b) => b.file_exchange_id).filter(Boolean)));
      const rows = (fx || []).filter((d) => d.storage_path);
      // This year's first, then the rest — a mis-foldered original is still reachable.
      rows.sort((a, b) => (String(b.year_subfolder) === String(year)) - (String(a.year_subfolder) === String(year)));
      setDocs(rows);
    })();
  }, [parcel.id, parcel.property_id, year]);

  async function scan(doc) {
    setPicked(doc); setPhase('scanning'); setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session && session.access_token;
      const [full, table] = await Promise.all([
        extract(doc.storage_path, 'taxbill', token),
        extract(doc.storage_path, 'rate_table', token).catch(() => null),
      ]);
      const merged = mergeRateTable(full.jurisdictions, table);
      const tidy = cleanBillLines(merged.lines);
      setResult({ lines: tidy.lines, removed: [...merged.removed, ...tidy.removed], paired: merged.paired,
        taxYear: full.tax_year ? String(full.tax_year).slice(0, 4) : null, taxable: num(full.taxable_value), extracted: full });
      setPhase('review');
    } catch (e) {
      setError(e.message || String(e)); setPhase('pick');
    }
  }

  const editLine = (i, field, value) => setResult((r) => ({ ...r, lines: r.lines.map((l, k) => (k === i ? { ...l, [field]: value } : l)) }));

  async function save() {
    setPhase('saving'); setError('');
    const adv = splitJurisdictionLines(result.lines).sumRatedAmount;
    const { data, error: e } = await supabase.from('tax_bill_references').upsert({
      parcel_id: parcel.id, tax_year: String(year), file_exchange_id: picked.id,
      jurisdiction_rates: dropEmptyLines(result.lines), total_ad_valorem: adv == null ? null : Math.round(adv * 100) / 100,
      taxable_value: result.taxable, extracted_fields: result.extracted,
      scanned_by: staffUser?.id || null, scanned_at: new Date().toISOString(),
    }, { onConflict: 'parcel_id,tax_year,file_exchange_id' }).select('id');
    // An RLS-blocked write returns 0 rows and NO error — the absence of an error is not proof it saved.
    if (e || !data?.length) { setError(`Not saved: ${e ? e.message : 'no row written'}`); setPhase('review'); return; }
    onSaved && onSaved();
    onClose();
  }

  // An ENTERED total: the original's printed ad valorem, typed. Checked for an affected row like every write here.
  async function saveTyped() {
    const total = num(typed.total);
    if (total == null || total <= 0) { setError("Enter the original bill's total ad valorem exactly as printed."); return; }
    setPhase('saving'); setError('');
    const { data, error: e } = await supabase.from('tax_bill_references').insert({
      parcel_id: parcel.id, tax_year: String(year), source: 'entered', total_ad_valorem: Math.round(total * 100) / 100,
      note: typed.note.trim() || null, scanned_by: staffUser?.id || null, scanned_at: new Date().toISOString(),
    }).select('id');
    if (e || !data?.length) { setError(`Not saved: ${e ? e.message : 'no row written'}`); setPhase('pick'); return; }
    onSaved && onSaved();
    onClose();
  }

  const unit = result ? resolveRateUnit(result.lines) : null;
  const adv = result ? splitJurisdictionLines(result.lines).sumRatedAmount : null;
  const bases = result ? [...new Set(result.lines.map((l) => num(l.taxable_value)).filter((v) => v))] : [];
  const btn = { padding: '7px 14px', borderRadius: '8px', border: `1px solid ${c.border}`, background: 'transparent', color: c.text, cursor: 'pointer', fontSize: '12px', fontWeight: 600 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '20px 22px', width: '760px', maxWidth: '94vw', maxHeight: '88vh', overflow: 'auto', color: c.text }}>
        <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>Pre-appeal reference bill — {year}</div>
        <div style={{ fontSize: '12px', color: c.muted, marginBottom: '14px', lineHeight: 1.5 }}>
          Reads a filed original (pre-appeal) bill so savings can be taken off the bills: original − revised, to the cent.
          It is stored only as a reference — it is not a bill, is never transmitted and never appears in Approvals.
        </div>
        {error && <div style={{ fontSize: '12px', color: 'var(--red)', fontWeight: 600, marginBottom: '10px' }}>⚠ {error}</div>}

        {phase === 'pick' && (
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
            {[['scan', 'Scan the filed original'], ['enter', 'Type its total']].map(([k, label]) => (
              <button key={k} type="button" onClick={() => { setMode(k); setError(''); }}
                style={{ ...btn, ...(mode === k ? { background: 'var(--red)', color: '#fff', border: 'none' } : {}) }}>{label}</button>
            ))}
          </div>
        )}

        {phase === 'pick' && mode === 'enter' && (
          <div style={{ display: 'grid', gap: '10px', maxWidth: '420px' }}>
            <label style={{ fontSize: '12px' }}>Original bill — total ad valorem, as printed
              <input value={typed.total} onChange={(e) => setTyped((t) => ({ ...t, total: e.target.value }))} placeholder="e.g. 467,639.38"
                style={{ display: 'block', width: '100%', marginTop: '4px', padding: '6px 8px', fontFamily: 'monospace', border: `1px solid ${c.border}`, borderRadius: '6px', background: 'transparent', color: c.text }} />
            </label>
            <label style={{ fontSize: '12px' }}>Note (optional)
              <input value={typed.note} onChange={(e) => setTyped((t) => ({ ...t, note: e.target.value }))} placeholder="from original bill, filed"
                style={{ display: 'block', width: '100%', marginTop: '4px', padding: '6px 8px', border: `1px solid ${c.border}`, borderRadius: '6px', background: 'transparent', color: c.text }} />
            </label>
            <div style={{ fontSize: '11px', color: c.muted }}>Savings become this total − the revised bill's ad valorem. Ad valorem only — not the combined total with non-ad valorem assessments.</div>
          </div>
        )}

        {phase === 'pick' && mode === 'scan' && (
          docs == null ? <div style={{ fontSize: '12px', color: c.muted }}>Loading filed tax bills…</div>
          : !docs.length ? <div style={{ fontSize: '12px', color: c.muted }}>No filed tax-bill documents for this parcel. File the original in Document Intake first.</div>
          : (
            <div>
              {docs.map((d) => {
                const live = liveIds.has(d.id);
                return (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0', borderBottom: `1px solid ${c.border}`, fontSize: '12px' }}>
                    <span style={{ flex: 1 }}>{d.file_name}</span>
                    <span style={{ color: c.muted }}>{d.year_subfolder || 'no year'}</span>
                    {live ? <span style={{ color: c.muted }}>the live {year} bill</span>
                      : <button type="button" style={btn} onClick={() => scan(d)}>Scan as reference</button>}
                  </div>
                );
              })}
            </div>
          )
        )}

        {phase === 'scanning' && <div style={{ fontSize: '12px', color: c.muted }}>Reading {picked?.file_name}…</div>}
        {phase === 'saving' && mode === 'enter' && <div style={{ fontSize: '12px', color: c.muted }}>Saving…</div>}

        {(phase === 'review' || phase === 'saving') && result && (
          <div>
            <div style={{ fontSize: '12px', marginBottom: '8px', lineHeight: 1.6 }}>
              <b>{picked.file_name}</b> · ad valorem <b>{fmtc(adv)}</b> · {result.lines.length} lines · rate in {unit?.confident ? unit.unit : 'an undetermined unit'}
              {bases.length > 0 && <> · billed on {bases.map((b) => b.toLocaleString('en-US')).join(' and ')}</>}
            </div>
            {result.taxYear && result.taxYear !== String(year) && (
              <div style={{ fontSize: '12px', color: 'var(--red)', fontWeight: 700, marginBottom: '8px' }}>⚠ This bill reads as tax year {result.taxYear}, not {year}. Check it is the right document.</div>
            )}
            {!result.paired && (
              <div style={{ fontSize: '12px', color: 'var(--red)', fontWeight: 700, marginBottom: '8px' }}>⚠ Check names against the bill — the rate-table check could not confirm them.</div>
            )}
            {result.removed.length > 0 && (
              <div style={{ fontSize: '11px', color: c.muted, marginBottom: '8px' }}>
                Set aside: {result.removed.map((r) => `${r.line.name || 'unnamed'}${r.line.amount != null ? ` ${fmtc(num(r.line.amount))}` : ''}`).join(' · ')}
              </div>
            )}
            <JurisdictionBreakdown rates={result.lines} unit={unit?.confident ? unit.unit : 'percent'} darkMode={isDark} onChange={editLine} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button type="button" style={btn} onClick={() => { setPhase('pick'); setResult(null); }}>Back</button>
              <button type="button" style={{ ...btn, background: 'var(--red)', color: '#fff', border: 'none' }} disabled={phase === 'saving'} onClick={save}>
                {phase === 'saving' ? 'Saving…' : 'Save as pre-appeal reference'}
              </button>
            </div>
          </div>
        )}

        {phase === 'pick' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
            <button type="button" style={btn} onClick={onClose}>Close</button>
            {mode === 'enter' && (
              <button type="button" style={{ ...btn, background: 'var(--red)', color: '#fff', border: 'none' }} onClick={saveTyped}>Save as pre-appeal reference</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
